import { RefObject, useEffect, useRef } from 'react'
import { useRouter } from '@moonpig/web-core-routing'

const SHOW_RECIPIENTS_PARAM = 'showRecipients'

type ProductListingPage =
  | 'content'
  | 'search'
  | 'dynamic'
  | 'pay-per-click'
  | 'collections'

type UseAutoOpenRecipientsArgs = {
  triggerRef: RefObject<HTMLButtonElement>
  mdBreakpointPx: number
  onOpenDesktop: () => void
  onOpenMobile: () => void
}

const clearShowRecipientsParam = (router: ReturnType<typeof useRouter>) => {
  const { name, params } = router.getCurrentRoute<ProductListingPage>()
  /* istanbul ignore if */
  if (!name) return

  const { [SHOW_RECIPIENTS_PARAM]: _removed, ...restParams } = params as {
    [SHOW_RECIPIENTS_PARAM]?: string
  } & typeof params

  router.replace({
    name,
    params: restParams,
    shallow: true,
    scroll: false,
  })
}

export const useAutoOpenRecipients = ({
  triggerRef,
  mdBreakpointPx,
  onOpenDesktop,
  onOpenMobile,
}: UseAutoOpenRecipientsArgs) => {
  const router = useRouter()
  const onOpenDesktopRef = useRef(onOpenDesktop)
  const onOpenMobileRef = useRef(onOpenMobile)
  onOpenDesktopRef.current = onOpenDesktop
  onOpenMobileRef.current = onOpenMobile

  useEffect(() => {
    const shouldOpen =
      new URLSearchParams(window.location.search).get(SHOW_RECIPIENTS_PARAM) ===
      'true'
    if (!shouldOpen) return

    const isMobileViewport = !window.matchMedia(
      `(min-width: ${mdBreakpointPx}px)`,
    ).matches

    if (isMobileViewport) {
      onOpenMobileRef.current()
    } else {
      onOpenDesktopRef.current()
    }

    clearShowRecipientsParam(router)
  }, [mdBreakpointPx, router, triggerRef])
}
